import React, { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import clsx from 'clsx';
import Image from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import { ChangeStoreIcon } from 'components/icons';
import { trackSelectPromotion, trackViewPromotion } from 'helpers/analytics/ga4Events';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { mobile, desktop } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';
import { usePathname } from 'i18n/routing';
import { Reference } from 'types/reference';

export interface Props {
  bgColor?: string;
  ctaFontSizeLg?: number;
  ctaFontSizeSm?: number;
  ctaText?: string;
  ctaTextColor?: string;
  imageAlt?: string;
  imageSrc: string;
  subtitle?: string;
  subtitleColor?: string;
  subtitleSizeLg?: number;
  subtitleSizeSm?: number;
  target?: Reference;
  title: string;
  titleColor?: string;
  titleSizeLg?: number;
  titleSizeSm?: number;
}

const OccasionBannerCard: React.FC<Props> = ({
  bgColor,
  ctaFontSizeLg,
  ctaFontSizeSm,
  ctaText,
  ctaTextColor,
  imageAlt,
  imageSrc,
  subtitle,
  subtitleColor,
  subtitleSizeLg,
  subtitleSizeSm,
  target,
  title,
  titleColor,
  titleSizeLg,
  titleSizeSm,
}) => {
  const [isDesktop] = useMediaQuery(desktop);
  const [isMobileSize] = useMediaQuery(mobile);
  const tileImageSizes = useImageSizes({
    sm: 0.3,
    md: 0.2,
    lg: 0.3,
    defaultSize:0.3,
    maxWidth: 564
  });
  // GA4: Promotion tracking
  const bannerRef = React.useRef<HTMLElement>(null);
  const viewFired = React.useRef(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !viewFired.current) {
            trackViewPromotion({
              creativeName: imageAlt || title,
              creativeSlot: 'occasion_banner',
              promotionId: `occasion_banner_${cleanString(title)}`,
              promotionName: title,
            });
            viewFired.current = true;
          }
        });
      },
      { threshold: 0.5 },
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => observer.disconnect();
  }, [imageAlt, title]);

  const handleCtaClick = () => {
    trackSelectPromotion({
      creativeName: imageAlt || title,
      creativeSlot: 'occasion_banner',
      promotionId: `occasion_banner_${cleanString(title)}`,
      promotionName: title,
    });
  };
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSearch = useMemo(
    () => pathname === '/search' && Boolean(searchParams.get('query')),
    [pathname, searchParams.get('query')],
  );
  const { titleSize, subTitleSize, ctaTextSize, sectionBgColor } = useMemo(() => {
    const titleSize = isDesktop ? (titleSizeLg ?? 40) : (titleSizeSm ?? 28);
    const subTitleSize = isDesktop ? (subtitleSizeLg ?? 28) : (subtitleSizeSm ?? 16);
    const ctaTextSize = isDesktop ? (ctaFontSizeLg ?? 28) : (ctaFontSizeSm ?? 12);
    const sectionBgColor = bgColor ?? '#F8F9FA';
    return {
      titleSize,
      subTitleSize,
      ctaTextSize,
      sectionBgColor,
    };
  }, [isDesktop, titleSizeLg, titleSizeSm, subtitleSizeLg, subtitleSizeSm, ctaFontSizeLg, ctaFontSizeSm]);
  const aspectRatio = isMobileSize ? 'aspect-[1.17/1]' : 'aspect-[1.34/1]';

  console.info("OccasionBannerCard: ", { title, subtitle, imageSrc, target, ctaText, titleSize, subTitleSize, ctaTextSize, sectionBgColor });

  return (
    <div className="mx-0 px-20 sm:mx-24 sm:px-0 md:mx-75 lg:mx-76 xl:mx-0" data-ffid="ctr-occasion-banner-wrapper">
      <section
        ref={bannerRef}
        className={`container relative flex w-full flex-col rounded-xl sm:flex-row sm:items-center md:rounded-2xl`}
        style={{ backgroundColor: sectionBgColor }}
        data-ffid="ctr-occasion-banner"
      >
        <div
          className={clsx('relative h-auto w-full overflow-hidden rounded-xl md:w-1/2 md:rounded-2xl', aspectRatio)}
          data-ffid="ctr-occasion-banner-media"
        >
          <Link
            link={target}
            title={imageAlt || title}
            data-testid="occasion-banner-img-link"
            data-ffid={`lnk-occasion-banner-img-link`}
            onClick={handleCtaClick}
          >
            <Image
              src={imageSrc}
              alt={imageAlt || title}
              className="size-full rounded-xl object-cover md:rounded-2xl"
              data-ffid="img-occasion-banner"
              unoptimized={false}
              fill
              priority={isSearch}
              loading={isSearch ? 'eager' : 'lazy'}
              fetchPriority={isSearch ? 'high' : 'auto'}
              sizes={tileImageSizes}
            />
          </Link>
        </div>
        <div className="flex w-full flex-col max-sm:text-center gap-12 sm:gap-16 md:gap-24 px-16 py-24 xs:px-24 md:px-36 md:py-32 lg:px-50 md:w-1/2">
          <h2
            className="leading-[1.2]"
            style={{
              fontSize: titleSize,
              color: titleColor ?? '#333333',
            }}
            data-ffid="lbl-occasion-banner-title"
          >
            {title}
          </h2>
          {subtitle && (
            <p
              style={{
                fontSize: subTitleSize,
                color: subtitleColor ?? '#333333',
              }}
              data-ffid="lbl-occasion-banner-subtitle"
            >
              {subtitle}
            </p>
          )}
          {target && (
            <Link
              link={target}
              title={ctaText}
              data-testid="occasion-banner-link"
              data-ffid={`lnk-occasion-banner-link`}
              className={`flex items-center max-sm:justify-center`}
              onClick={handleCtaClick}
              style={{
                color: ctaTextColor ?? '#0B57D0',
                fontSize: ctaTextSize,
              }}
            >
              {ctaText}
              <ChangeStoreIcon className={`w-${ctaTextSize} ml-4`} />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default OccasionBannerCard;
